import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Upload, Radio, Tooltip, Button, Image as ImageAntd } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Palette, usePalette } from 'color-thief-react';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { getBase64Image } from '@utils/formatters';
import { CSSProperties } from 'styled-components';

import * as S from './styles';

interface LogoUploadProps {
  setFile: (file: any) => void;
  setColor: React.Dispatch<React.SetStateAction<string | undefined>>;
  type: 'light' | 'dark';
  style?: CSSProperties;
}

const LogoUpload = ({ setColor, setFile, type, style }: LogoUploadProps) => {
  const { t } = useTranslation('main');
  const inputColorRef = useRef<HTMLInputElement>(null);
  const {
    logoImgName,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const [fileList, setFileList] = useState<any>([]);
  const [image, setImage] = useState<HTMLImageElement>();
  const [whenEditing, setWhenEditing] = useState<{
    url: string;
    imgInBase64: string;
  }>();
  const [chosenColor, setChosenColor] = useState<string>();
  const [imageUrl, setImageUrl] = useState<string>();
  const [colors, setColors] = useState<string[]>();

  const { data: initialColors } = usePalette(
    fileList[0]?.thumbUrl ?? fileList[0]?.url,
    4,
    'hex',
  );

  const treatedColors = [...new Set(initialColors ?? colors)];

  useEffect(() => {
    if (chosenColor) {
      setColor(chosenColor);
    }
  }, [
    chosenColor,
    customerLogged?.darkMainColor,
    customerLogged?.mainColor,
    setColor,
    type,
  ]);

  useEffect(() => {
    if (customerLogged?.domain && customerLogged?.domain !== '') {
      const isDarkTheme = type === 'dark';

      setChosenColor(
        isDarkTheme ? customerLogged?.darkMainColor : customerLogged?.mainColor,
      );

      const { protocol, hostname } = window.location;
      const isLocalhost = hostname === 'localhost';
      const localhostHostname = process.env.REACT_APP_HML_HOSTNAME;

      const url = `${isLocalhost ? 'https:' : protocol}//${
        isLocalhost ? localhostHostname : hostname
      }/images/${customerLogged?.domain}${isDarkTheme ? '-dark' : ''}`;

      getBase64Image(url, img => setWhenEditing({ url, imgInBase64: img }));
    }
  }, [customerLogged, logoImgName, type]);

  useEffect(() => {
    if (whenEditing?.imgInBase64) {
      setFileList([
        {
          uid: '-1',
          name: 'logo.png',
          status: 'done',
          url: whenEditing.url,
          thumbUrl: whenEditing?.imgInBase64,
        },
      ]);
      setImageUrl(whenEditing?.imgInBase64);
    }
  }, [whenEditing]);

  useEffect(() => {
    if (fileList.length === 0) {
      setFile(undefined);
    } else if (image && chosenColor) {
      setFile(image);
      setColor(chosenColor);
    }
  }, [chosenColor, setFile, image, setColor, fileList.length]);

  const onPreview = useCallback(async (prevFile: any) => {
    let src = prevFile.url;
    if (!src) {
      src = await new Promise(resolve => {
        const reader = new FileReader();
        reader.readAsDataURL(prevFile.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const img = new Image();
    img.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(img.outerHTML);
  }, []);

  const uploadImage = useCallback(async (options: any) => {
    setImage(options?.file);
    options?.onSuccess();
  }, []);

  const onChange = useCallback(({ fileList: newFileList }: any) => {
    setFileList(newFileList);

    if (newFileList.length > 0) {
      const lastFile = newFileList[newFileList.length - 1];
      if (lastFile.originFileObj) {
        setImageUrl(URL.createObjectURL(lastFile.originFileObj));
      }
    } else {
      setChosenColor(undefined);
      setColors(undefined);
      setImageUrl(undefined);
    }
  }, []);

  const handleChangeColor = useCallback(({ target: { value } }: any) => {
    setChosenColor(value);
  }, []);

  return (
    <S.LogoUploadContainer style={style}>
      <S.LogoUploadWrapper>
        <p>
          {t('personalization.logo')}
          <Tooltip title={t('personalization.logo-help')}>
            <Button type="link" icon={<InfoCircleOutlined />} />
          </Tooltip>
        </p>
        <div>
          <Upload
            maxCount={1}
            listType="picture-card"
            fileList={fileList}
            onPreview={onPreview}
            onChange={onChange}
            customRequest={uploadImage}
            accept=".png"
          >
            <FileAddOutlined style={{ fontSize: 32, opacity: '0.4' }} />
          </Upload>
        </div>
        {fileList?.length > 0 && imageUrl && (
          <S.LogoPreview type={type}>
            <S.PreviewAlert>
              {t('personalization.preview') as string}
              <Tooltip title={t('personalization.logo-alert')}>
                <Button type="link" icon={<ExclamationCircleOutlined />} />
              </Tooltip>
            </S.PreviewAlert>
            <div className="logo-preview-content">
              <Palette src={imageUrl} colorCount={4} format="hex">
                {({ data }) => {
                  setColors(data);
                  return <ImageAntd src={imageUrl} preview={false} />;
                }}
              </Palette>
            </div>
          </S.LogoPreview>
        )}
      </S.LogoUploadWrapper>
      <S.LogoColorsWrapper
        color={chosenColor}
        hasColors={colors ? colors.length > 0 : false}
      >
        <Tooltip title={!treatedColors && t('personalization.upload-a-logo')}>
          <p>
            {t('personalization.primary-color')}
            {treatedColors && (
              <Tooltip title={t('personalization.primary-color-help')}>
                <Button type="link" icon={<InfoCircleOutlined />} />
              </Tooltip>
            )}
          </p>
          <div className="radio-group-wrapper">
            <S.ChosenColor chosenColor={chosenColor}>
              {fileList?.length > 0 && (
                <Tooltip title={t('personalization.customColor')}>
                  <EditOutlined
                    onClick={() => {
                      if (inputColorRef.current) {
                        inputColorRef.current.click();
                      }
                    }}
                  />
                </Tooltip>
              )}

              <input
                ref={inputColorRef}
                type="color"
                value={chosenColor}
                onChange={e => setChosenColor(e.target.value)}
                style={{ display: 'none' }}
              />
            </S.ChosenColor>
            <Radio.Group
              onChange={handleChangeColor}
              className="radio-group-colors"
              value={chosenColor}
            >
              {treatedColors?.map((color, index) => (
                <S.RadioButton
                  key={color ?? index}
                  value={color}
                  style={{ background: color }}
                />
              ))}
            </Radio.Group>
          </div>
        </Tooltip>

        {fileList?.length > 0 && chosenColor && (
          <S.LogoPreview type={type}>
            <S.PreviewAlert>
              {t('personalization.preview') as string}
              <Tooltip title={t('personalization.color-alert')}>
                <Button type="link" icon={<ExclamationCircleOutlined />} />
              </Tooltip>
            </S.PreviewAlert>
            <div className="logo-preview-content">
              <S.LogoColorPreviewContent type={type} color={chosenColor}>
                <div className="logo-upload-color-preview">
                  {t('personalization.text')}
                </div>
                <Button icon={<CheckCircleOutlined />} />
              </S.LogoColorPreviewContent>
            </div>
          </S.LogoPreview>
        )}
      </S.LogoColorsWrapper>
    </S.LogoUploadContainer>
  );
};

export default LogoUpload;
